import type { ReactNode } from 'react';

interface AlertProps {
  children: ReactNode;
  variant?: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const Alert = ({ 
  children, 
  variant = 'info', 
  title, 
  dismissible = false, 
  onDismiss, 
  className = '' 
}: AlertProps) => {
  const baseClasses = 'rounded-lg border p-4 relative';

  const variantClasses = {
    success: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100',
    error: 'border-red-500/40 bg-red-500/10 text-red-100',
    info: 'border-oro-gold/40 bg-oro-gold/10 text-oro-gold-text',
    warning: 'border-amber-500/40 bg-amber-500/10 text-amber-100'
  };

  const titleClasses = {
    success: 'text-emerald-300',
    error: 'text-red-400',
    info: 'text-oro-gold',
    warning: 'text-amber-300'
  };

  const iconClasses = {
    success: 'text-emerald-300',
    error: 'text-red-400', 
    info: 'text-oro-gold',
    warning: 'text-amber-300'
  };

  const getIcon = (variant: string) => {
    switch (variant) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${className}`;

  return (
    <div className={classes} role="alert">
      <div className="flex items-start gap-3">
        <span className={`text-lg ${iconClasses[variant]} shrink-0 mt-0.5`}>
          {getIcon(variant)}
        </span>
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`text-sm font-semibold mb-1 ${titleClasses[variant]}`}>
              {title}
            </div>
          )}
          <div className="text-sm">
            {children}
          </div>
        </div>
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={`${iconClasses[variant]} hover:opacity-70 text-lg leading-none shrink-0`}
            aria-label="Dismiss alert"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;